import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Play, Calendar, ChevronRight, ChevronLeft, 
  ArrowRight, Newspaper, Video, Clock, MapPin, Pause, X
} from "lucide-react";
import { AdBanner } from "./AdBanner";
import { PhotoGallerySection } from "./PhotoGallerySection";
import { SocialFeedSection } from "./SocialFeedSection";
import { SocialMediaWidget } from "./SocialMediaWidget";
import { CustomSectionRenderer } from "./CustomSections";
import { HomepagePollSection } from "./HomepagePollSection";
import { ContinueWatchingSection } from "./ContinueWatchingSection";
import ScoreBirdGameCast from "./ScoreBirdGameCast";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ============================================
// COLOR SCHEME: Black, White, Orange
// ============================================
const COLORS = {
  primary: "#F97316", // Orange-500
  primaryDark: "#EA580C", // Orange-600
  primaryLight: "#FB923C", // Orange-400
  black: "#000000",
  white: "#FFFFFF",
  grayDark: "#18181B", // Zinc-900
  grayMid: "#27272A", // Zinc-800
  grayLight: "#3F3F46", // Zinc-700
  grayText: "#A1A1AA", // Zinc-400
};

// ============================================
// SECTION 1: NEXT EVENT COUNTDOWN (Hero)
// ============================================
const NextEventSection = ({ event, liveBroadcast, heroData = {} }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  // Extract hero configuration
  const {
    layout = "standard",
    background_image = "",
    image_position_x = 50,
    image_position_y = 50,
    image_scale = 100,
    image_blur = 0,
    overlay_opacity = 70
  } = heroData;

  // Determine if we should use compact layout
  const isCompact = layout === "compact";

  // Build background style for the image layer only
  const backgroundImageStyle = background_image ? {
    backgroundImage: `url(${background_image})`,
    backgroundPosition: `${image_position_x}% ${image_position_y}%`,
    backgroundSize: `${image_scale}%`,
    backgroundRepeat: 'no-repeat',
    filter: image_blur > 0 ? `blur(${image_blur}px)` : undefined
  } : {};

  // Build overlay style
  const overlayStyle = background_image ? {
    background: `linear-gradient(to right, rgba(0,0,0,${overlay_opacity/100}), rgba(0,0,0,${Math.max(0, overlay_opacity/100 - 0.3)}))`
  } : {};

  useEffect(() => {
    if (!event?.nextBroadcast) return;
    
    const broadcast = event.nextBroadcast;
    let targetDate;
    
    // Handle different date/time formats
    if (broadcast.scheduled_datetime) {
      targetDate = new Date(broadcast.scheduled_datetime);
    } else if (broadcast.scheduled_date && broadcast.scheduled_time) {
      const timeStr = broadcast.scheduled_time.includes(':') 
        ? broadcast.scheduled_time 
        : `${broadcast.scheduled_time}:00`;
      targetDate = new Date(`${broadcast.scheduled_date}T${timeStr}`);
    } else if (broadcast.scheduled_date) {
      targetDate = new Date(`${broadcast.scheduled_date}T12:00:00`);
    } else {
      return;
    }
    
    if (isNaN(targetDate.getTime())) {
      console.warn("Invalid broadcast date:", broadcast);
      return;
    }
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const target = targetDate.getTime();
      const diff = target - now;

      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          mins: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          secs: Math.floor((diff % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, mins: 0, secs: 0 });
      }
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [event]);

  // Show live broadcast if available
  if (liveBroadcast) {
    const homeTeam = (liveBroadcast.home_team && typeof liveBroadcast.home_team === 'object') ? liveBroadcast.home_team : { name: typeof liveBroadcast.home_team === 'string' ? liveBroadcast.home_team : '' };
    const awayTeam = (liveBroadcast.away_team && typeof liveBroadcast.away_team === 'object') ? liveBroadcast.away_team : { name: typeof liveBroadcast.away_team === 'string' ? liveBroadcast.away_team : '' };
    
    return (
      <section 
        className={`relative border-b-4 border-orange-500 overflow-hidden ${isCompact ? 'py-6' : 'py-12 md:py-16'}`} 
        data-testid="hero-section"
      >
        {/* Background Image Layer (blurred separately) */}
        {background_image && (
          <div className="absolute inset-0" style={backgroundImageStyle} />
        )}
        {/* Background overlay */}
        {background_image && (
          <div className="absolute inset-0" style={overlayStyle} />
        )}
        {!background_image && <div className="absolute inset-0 bg-black" />}
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
          <div className="flex items-center justify-between gap-4">
            {/* Home Team Logo - Left */}
            <div className="hidden md:flex flex-1 justify-start">
              <div 
                className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4 border-2"
                style={{ borderColor: homeTeam?.color || '#f97316' }}
              >
                {homeTeam?.logo_url ? (
                  <img 
                    src={homeTeam.logo_url} 
                    alt={homeTeam.name} 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-white font-black text-4xl lg:text-5xl">
                    {homeTeam?.abbreviation || homeTeam?.name?.charAt(0) || 'H'}
                  </span>
                )}
              </div>
            </div>
            
            {/* Center Content */}
            <div className="flex flex-col items-center text-center flex-shrink-0">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-500 font-bold uppercase tracking-wider">Live Now</span>
              </div>
              <div className="text-orange-500 text-sm font-bold uppercase tracking-wider mb-2">
                {liveBroadcast.sport}
              </div>
              <h1 className={`font-heading uppercase tracking-tight text-white mb-4 ${isCompact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl lg:text-6xl'}`}>
                {liveBroadcast.title}
              </h1>
              
              {/* Mobile Team Logos */}
              <div className="flex md:hidden items-center justify-center gap-6 mb-4">
                <div 
                  className="w-16 h-16 rounded-full bg-zinc-900/50 flex items-center justify-center p-2 border-2 overflow-hidden"
                  style={{ borderColor: homeTeam?.color || '#f97316' }}
                >
                  {homeTeam?.logo_url ? (
                    <img src={homeTeam.logo_url} alt={homeTeam.name} className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-white font-bold text-lg">{homeTeam?.abbreviation || homeTeam?.name?.charAt(0)}</span>
                  )}
                </div>
                <span className="text-zinc-500 text-sm font-bold">VS</span>
                <div 
                  className="w-16 h-16 rounded-full bg-zinc-900/50 flex items-center justify-center p-2 border-2 overflow-hidden"
                  style={{ borderColor: awayTeam?.color || '#3b82f6' }}
                >
                  {awayTeam?.logo_url ? (
                    <img src={awayTeam.logo_url} alt={awayTeam.name} className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-white font-bold text-lg">{awayTeam?.abbreviation || awayTeam?.name?.charAt(0)}</span>
                  )}
                </div>
              </div>
              
              <Link 
                to={`/game/${liveBroadcast.id}`}
                className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-black font-bold uppercase hover:bg-orange-400 transition-colors"
              >
                Watch Live <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            {/* Away Team Logo - Right */}
            <div className="hidden md:flex flex-1 justify-end">
              <div 
                className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4 border-2"
                style={{ borderColor: awayTeam?.color || '#3b82f6' }}
              >
                {awayTeam?.logo_url ? (
                  <img 
                    src={awayTeam.logo_url} 
                    alt={awayTeam.name} 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-white font-black text-4xl lg:text-5xl">
                    {awayTeam?.abbreviation || awayTeam?.name?.charAt(0) || 'A'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show a branded welcome hero when no event
  if (!event) {
    return (
      <section 
        className={`relative border-b-4 border-orange-500 overflow-hidden ${isCompact ? 'py-6' : 'py-12 md:py-16'}`} 
        data-testid="hero-section"
      >
        {background_image && (
          <div className="absolute inset-0" style={backgroundImageStyle} />
        )}
        {background_image && (
          <div className="absolute inset-0" style={overlayStyle} />
        )}
        {!background_image && <div className="absolute inset-0 bg-black" />}
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center py-8">
            <h1 className="text-4xl md:text-6xl font-heading uppercase tracking-tight text-white mb-4">
              Antler Sports Network
            </h1>
            <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-8">
              Your home for high school &amp; collegiate sports coverage. Live games, highlights, and more.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/schedule" className="px-6 py-3 bg-orange-500 text-black font-bold uppercase text-sm tracking-wider hover:bg-orange-400 transition-colors">
                View Schedule
              </Link>
              <Link to="/watch" className="px-6 py-3 border border-zinc-600 text-white font-bold uppercase text-sm tracking-wider hover:border-orange-500 transition-colors">
                Watch On Demand
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const broadcast = event.nextBroadcast;

  // COMPACT LAYOUT - Horizontal with teams and countdown side by side
  if (isCompact) {
    return (
      <section 
        className="relative border-b-4 border-orange-500 overflow-hidden py-4"
        data-testid="hero-section"
      >
        {/* Background Image Layer (blurred separately) */}
        {background_image && (
          <div className="absolute inset-0" style={backgroundImageStyle} />
        )}
        {/* Background overlay */}
        {background_image && (
          <div className="absolute inset-0" style={overlayStyle} />
        )}
        {!background_image && <div className="absolute inset-0 bg-black" />}
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
          <div className="flex items-center justify-between gap-4">
            {/* Left Side: Teams Matchup - Horizontal */}
            <div className="flex items-center gap-6">
              {/* Away Team */}
              {broadcast?.away_team?.name && (
                <div className="flex items-center gap-3">
                  {broadcast.away_team.logo_url ? (
                    <img src={broadcast.away_team.logo_url} alt="" className="w-14 h-14 md:w-16 md:h-16 object-contain" />
                  ) : (
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-zinc-800 flex items-center justify-center text-lg font-bold text-white">
                      {broadcast.away_team.abbreviation || broadcast.away_team.name?.charAt(0)}
                    </div>
                  )}
                  <span className="text-white font-heading uppercase text-lg md:text-xl font-bold">
                    {broadcast.away_team.abbreviation || broadcast.away_team.name}
                  </span>
                </div>
              )}

              {/* VS */}
              <div className="text-zinc-500 text-sm font-bold uppercase px-2">@</div>

              {/* Home Team */}
              {broadcast?.home_team?.name && (
                <div className="flex items-center gap-3">
                  {broadcast.home_team.logo_url ? (
                    <img src={broadcast.home_team.logo_url} alt="" className="w-14 h-14 md:w-16 md:h-16 object-contain" />
                  ) : (
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-zinc-800 flex items-center justify-center text-lg font-bold text-white">
                      {broadcast.home_team.abbreviation || broadcast.home_team.name?.charAt(0)}
                    </div>
                  )}
                  <span className="text-white font-heading uppercase text-lg md:text-xl font-bold">
                    {broadcast.home_team.abbreviation || broadcast.home_team.name}
                  </span>
                </div>
              )}

              {/* Fallback if no teams */}
              {!broadcast?.away_team?.name && !broadcast?.home_team?.name && (
                <h2 className="font-heading uppercase text-white text-xl font-bold">{event.name}</h2>
              )}
            </div>

            {/* Center: Sport & Date */}
            <div className="hidden md:flex flex-col items-center">
              <span className="text-orange-500 text-sm font-bold uppercase tracking-wider">
                {broadcast?.sport || event.sport || "Sports"}
              </span>
              <span className="text-white text-sm">
                {broadcast?.scheduled_time && broadcast?.scheduled_date
                  ? new Date(`${broadcast.scheduled_date}T${broadcast.scheduled_time}:00`).toLocaleString("en-US", {
                      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
                    })
                  : "TBD"
                }
              </span>
            </div>

            {/* Right Side: Countdown */}
            <div className="flex items-center gap-3">
              {Object.entries(timeLeft).map(([unit, value]) => (
                <div key={unit} className="text-center">
                  <div className="relative bg-zinc-900/80 border border-zinc-700 w-14 h-14 md:w-16 md:h-16">
                    <span className="absolute inset-0 flex items-center justify-center font-heading text-white tabular-nums text-2xl md:text-3xl font-bold">
                      {String(value).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="text-xs text-white uppercase tracking-wider mt-1 font-medium">
                    {unit.charAt(0)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // STANDARD LAYOUT - Full vertical layout
  return (
    <section 
      className="relative border-b-4 border-orange-500 overflow-hidden py-12 md:py-16"
      data-testid="hero-section"
    >
      {/* Background Image Layer (blurred separately) */}
      {background_image && (
        <div className="absolute inset-0" style={backgroundImageStyle} />
      )}
      {/* Background overlay */}
      {background_image && (
        <div className="absolute inset-0" style={overlayStyle} />
      )}
      {!background_image && <div className="absolute inset-0 bg-black" />}
      
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center mb-6">
          <span className="text-zinc-500 text-sm uppercase tracking-wider">Next Event</span>
        </div>

        <div className="flex flex-col items-center">
          <div className="text-orange-500 text-sm font-bold uppercase tracking-wider mb-4">
            {broadcast?.sport || event.sport || "Sports"}
          </div>

          {/* Team Matchup */}
          <div className="flex items-center justify-center gap-6 md:gap-12 mb-8">
            {/* Away Team */}
            {broadcast?.away_team?.name && (
              <div className="text-center">
                {broadcast.away_team.logo_url ? (
                  <img src={broadcast.away_team.logo_url} alt="" className="w-20 h-20 md:w-28 md:h-28 object-contain mx-auto mb-3" />
                ) : (
                  <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3 text-2xl font-bold text-white">
                    {broadcast.away_team.abbreviation || broadcast.away_team.name?.charAt(0)}
                  </div>
                )}
                <h3 className="text-white font-heading uppercase text-lg md:text-xl">
                  {broadcast.away_team.name}
                </h3>
              </div>
            )}

            {/* VS / AT */}
            <div className="text-zinc-600 text-sm font-bold uppercase">at</div>

            {/* Home Team */}
            {broadcast?.home_team?.name && (
              <div className="text-center">
                {broadcast.home_team.logo_url ? (
                  <img src={broadcast.home_team.logo_url} alt="" className="w-20 h-20 md:w-28 md:h-28 object-contain mx-auto mb-3" />
                ) : (
                  <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3 text-2xl font-bold text-white">
                    {broadcast.home_team.abbreviation || broadcast.home_team.name?.charAt(0)}
                  </div>
                )}
                <h3 className="text-white font-heading uppercase text-lg md:text-xl">
                  {broadcast.home_team.name}
                </h3>
              </div>
            )}

            {/* Fallback if no teams */}
            {!broadcast?.away_team?.name && !broadcast?.home_team?.name && (
              <h2 className="font-heading uppercase tracking-tight text-white text-3xl md:text-5xl">
                {event.name}
              </h2>
            )}
          </div>

          {/* Date/Time */}
          <div className="flex items-center gap-4 text-zinc-400 text-sm mb-8">
            <Clock className="w-4 h-4" />
            <span>
              {broadcast?.scheduled_time && broadcast?.scheduled_date
                ? new Date(`${broadcast.scheduled_date}T${broadcast.scheduled_time}:00`).toLocaleString("en-US", {
                    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
                  })
                : "TBD"
              }
            </span>
          </div>

          {/* Countdown */}
          <div className="flex gap-4 md:gap-6">
            {Object.entries(timeLeft).map(([unit, value]) => (
              <div key={unit} className="text-center">
                <div className="flex items-center justify-center bg-zinc-900 border border-zinc-800 w-16 h-16 md:w-20 md:h-20">
                  <span className="font-heading text-white tabular-nums text-3xl md:text-4xl">
                    {String(value).padStart(2, '0')}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider mt-2">
                  {unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 2: TOP STORIES CAROUSEL
// ============================================
const TopStoriesSection = ({ articles }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (!isPlaying || articles.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % articles.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isPlaying, articles.length]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  // Hide section entirely when no articles
  if (!articles.length) return null;

  const currentArticle = articles[currentIndex];

  return (
    <section className="py-12 md:py-16 bg-zinc-900" data-testid="top-stories-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl md:text-4xl font-heading uppercase tracking-tight text-white">
            Top Stories
          </h2>
          <Link to="/news" className="text-orange-500 hover:text-orange-400 flex items-center gap-2 text-sm uppercase font-bold">
            View More <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Carousel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              <Link to={`/news/${currentArticle.slug}`} className="block group">
                <div className="grid md:grid-cols-2 gap-6 bg-zinc-800">
                  {/* Image */}
                  <div className="aspect-video md:aspect-auto overflow-hidden">
                    {currentArticle.featured_image ? (
                      <img
                        src={currentArticle.featured_image}
                        alt={currentArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full min-h-[300px] bg-zinc-700 flex items-center justify-center">
                        <Newspaper className="w-16 h-16 text-zinc-600" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <div className="text-zinc-500 text-sm mb-2">{formatDate(currentArticle.published_at)}</div>
                    <div className="text-orange-500 text-sm font-bold uppercase mb-3">
                      {currentArticle.category || "News"}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-heading uppercase tracking-tight text-white mb-4 group-hover:text-orange-500 transition-colors leading-tight">
                      {currentArticle.title}
                    </h3>
                    {currentArticle.excerpt && (
                      <p className="text-zinc-400 line-clamp-3 mb-6">
                        {currentArticle.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-4">
                      <span className="text-orange-500 font-bold uppercase text-sm hover:text-orange-400">
                        Recap
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {articles.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={() => setCurrentIndex(prev => prev === 0 ? articles.length - 1 : prev - 1)}
                className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {articles.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      idx === currentIndex ? 'bg-orange-500' : 'bg-zinc-600 hover:bg-zinc-500'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => setCurrentIndex(prev => (prev + 1) % articles.length)}
                className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 3: SCHEDULE (Simplified - No Events/Results Tabs)
// ============================================
const ScheduleSection = ({ broadcasts, channels, allBroadcasts }) => {
  const [sportFilter, setSportFilter] = useState("all");
  const [tab, setTab] = useState("upcoming");

  const formatDate = (broadcast) => {
    if (broadcast.scheduled_date && broadcast.scheduled_time) {
      const date = new Date(`${broadcast.scheduled_date}T${broadcast.scheduled_time}:00`);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      }
    }
    return "TBD";
  };

  const now = new Date();
  const upcoming = (broadcasts || []).filter(b => {
    const d = new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}:00`);
    return d > now || b.is_live;
  });
  const results = (allBroadcasts || []).filter(b => {
    const d = new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}:00`);
    return d < now && !b.is_live;
  }).sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));

  // Auto-select tab with content
  const activeTab = tab === "upcoming" && upcoming.length === 0 && results.length > 0 ? "results" : tab;
  const displayBroadcasts = activeTab === "results" ? results : upcoming;

  const sports = ["all", ...new Set(displayBroadcasts.map(b => b.sport).filter(Boolean))];
  const filteredBroadcasts = displayBroadcasts.filter(b => sportFilter === "all" || b.sport === sportFilter).slice(0, 6);

  // Hide section if no data at all
  if (upcoming.length === 0 && results.length === 0) return null;

  return (
    <section className="py-12 md:py-16 bg-black" data-testid="schedule-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-6">
            <h2 className="text-3xl md:text-4xl font-heading uppercase tracking-tight text-white">
              Schedule
            </h2>
            <div className="flex items-center border border-zinc-700">
              <button
                onClick={() => setTab("upcoming")}
                className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === "upcoming" ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-white"}`}
                data-testid="schedule-upcoming-tab"
              >
                Upcoming {upcoming.length > 0 && `(${upcoming.length})`}
              </button>
              <button
                onClick={() => setTab("results")}
                className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === "results" ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-white"}`}
                data-testid="schedule-results-tab"
              >
                Results {results.length > 0 && `(${results.length})`}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Sport Filter */}
            {sports.length > 1 && (
              <select
                value={sportFilter}
                onChange={(e) => setSportFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-white px-4 py-2 text-sm uppercase"
              >
                {sports.map(sport => (
                  <option key={sport} value={sport}>
                    {sport === "all" ? "All Sports" : sport}
                  </option>
                ))}
              </select>
            )}

            {/* View Full Schedule */}
            <Link 
              to="/schedule" 
              className="text-orange-500 hover:text-orange-400 text-sm font-bold uppercase flex items-center gap-1"
            >
              Full Schedule <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Schedule Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBroadcasts.length > 0 ? (
            filteredBroadcasts.map((broadcast) => {
              const channel = channels[broadcast.channel_id];
              const gameLink = `/game/${broadcast.id}`;
              const isCompleted = broadcast.is_completed || (broadcast.final_home_score != null && broadcast.final_away_score != null);
              // Handle both string and object team formats
              const homeTeam = typeof broadcast.home_team === 'object' ? broadcast.home_team : { name: broadcast.home_team || '' };
              const awayTeam = typeof broadcast.away_team === 'object' ? broadcast.away_team : { name: broadcast.away_team || '' };
              return (
                <Link
                  key={broadcast.id}
                  to={gameLink}
                  className="group block bg-zinc-900 border border-zinc-800 hover:border-orange-500 transition-colors"
                >
                  <div className="p-4">
                    {/* Sport & Date Row */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-orange-500 text-xs font-bold uppercase">
                        {broadcast.sport}
                      </span>
                      <div className="flex items-center gap-2">
                        {isCompleted && <span className="text-[10px] font-bold uppercase bg-zinc-700 text-zinc-300 px-1.5 py-0.5">Final</span>}
                        {broadcast.is_live && <span className="text-[10px] font-bold uppercase bg-red-600 text-white px-1.5 py-0.5 animate-pulse">Live</span>}
                        <span className="text-white text-sm font-bold">{formatDate(broadcast)}</span>
                      </div>
                    </div>

                    {/* Teams */}
                    <div className="space-y-3">
                      {/* Away Team */}
                      {awayTeam.name && (
                        <div className="flex items-center gap-3">
                          {awayTeam.logo_url ? (
                            <img src={awayTeam.logo_url} alt="" className="w-10 h-10 object-contain" />
                          ) : (
                            <div className="w-10 h-10 bg-zinc-800 rounded flex items-center justify-center text-xs font-bold text-white">
                              {awayTeam.abbreviation || awayTeam.name?.charAt(0)}
                            </div>
                          )}
                          <span className="text-white font-medium flex-1">{awayTeam.name}</span>
                          {isCompleted && <span className="text-white font-heading text-xl">{broadcast.final_away_score}</span>}
                        </div>
                      )}

                      {/* Home Team */}
                      {homeTeam.name && (
                        <div className="flex items-center gap-3">
                          {homeTeam.logo_url ? (
                            <img src={homeTeam.logo_url} alt="" className="w-10 h-10 object-contain" />
                          ) : (
                            <div className="w-10 h-10 bg-zinc-800 rounded flex items-center justify-center text-xs font-bold text-white">
                              {homeTeam.abbreviation || homeTeam.name?.charAt(0)}
                            </div>
                          )}
                          <span className="text-white font-medium flex-1">{homeTeam.name}</span>
                          {isCompleted && <span className="text-white font-heading text-xl">{broadcast.final_home_score}</span>}
                        </div>
                      )}

                      {/* Title fallback */}
                      {!awayTeam.name && !homeTeam.name && (
                        <div className="text-white font-medium">{broadcast.title}</div>
                      )}
                    </div>

                    {/* Channel Info */}
                    {channel && (
                      <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                        <span className="text-zinc-500 text-xs">{channel.name}</span>
                        <span className="text-orange-500 text-xs font-bold uppercase group-hover:underline">
                          {isCompleted ? "Recap" : "Watch Live"} →
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12 text-zinc-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{activeTab === "results" ? "No results yet" : "No upcoming games scheduled"}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 4: ATHLETES OF THE WEEK
// Features admin-uploaded background image with color fade and flagship sponsor
// ============================================
const AthletesSection = ({ athlete }) => {
  // Hide section entirely when no athlete
  if (!athlete || !athlete.is_enabled) return null;

  // Use school color or default to orange
  const schoolColor = athlete.school_color || "#F97316";
  const bgColor = `${schoolColor}`;

  return (
    <section 
      className="relative overflow-hidden" 
      data-testid="athletes-section"
    >
      {/* Background Image (admin-uploaded) on the right - full height */}
      {athlete.background_image_url && (
        <div className="absolute inset-0">
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-2/3 h-full">
            <img
              src={athlete.background_image_url}
              alt=""
              className="w-full h-full object-cover object-center"
            />
          </div>
          {/* Gradient fade from left color to image */}
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to right, ${bgColor} 0%, ${bgColor} 20%, ${bgColor}DD 35%, ${bgColor}88 50%, transparent 70%)`
            }}
          />
        </div>
      )}

      {/* Solid color background fallback if no background image */}
      {!athlete.background_image_url && (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: `${schoolColor}15` }}
        />
      )}

      <div className="relative z-10 grid md:grid-cols-2">
        {/* Left Column - Info */}
        <div className="flex flex-col justify-center p-8 md:p-12 lg:p-16 min-h-[450px]">
          {/* Top Row: School Logo + Flagship Sponsor */}
          <div className="flex items-center justify-between mb-6">
            {/* School Logo */}
            {athlete.school_logo_url ? (
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden shadow-lg"
                style={{ backgroundColor: schoolColor }}
              >
                <img src={athlete.school_logo_url} alt={athlete.school} className="w-12 h-12 object-contain" />
              </div>
            ) : (
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: schoolColor }}
              >
                <span className="text-black font-heading text-xl font-bold">
                  {athlete.school?.charAt(0) || "?"}
                </span>
              </div>
            )}

            {/* Flagship Sponsor */}
            {athlete.flagship_sponsor && (
              <div className="flex items-center gap-2">
                <span className="text-white/50 text-xs uppercase tracking-wider">Presented by</span>
                {athlete.flagship_sponsor.logo_url ? (
                  <a 
                    href={athlete.flagship_sponsor.url || "#"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    <img 
                      src={athlete.flagship_sponsor.logo_url} 
                      alt={athlete.flagship_sponsor.name} 
                      className="h-8 w-auto object-contain brightness-0 invert"
                    />
                  </a>
                ) : (
                  <span className="text-white font-bold text-sm">{athlete.flagship_sponsor.name}</span>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <div className="mb-4">
            <h2 className="text-xs text-white/70 uppercase tracking-widest font-bold">Athlete of the Week</h2>
          </div>

          {/* Athlete Info */}
          <div className="flex-1 flex flex-col justify-center">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight mb-2 text-white drop-shadow-lg">
              {athlete.name}
            </h3>
            <p className="text-lg text-white/90 font-bold uppercase tracking-wider mb-4">
              {athlete.sport} • {athlete.school}
            </p>
            
            {/* Description */}
            {athlete.description && (
              <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-md">
                {athlete.description}
              </p>
            )}

            {/* Read More Link */}
            {athlete.article && (
              <Link
                to={`/news/${athlete.article.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black font-bold uppercase text-xs tracking-wider hover:bg-white/90 transition-colors rounded self-start"
              >
                Read Full Story <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>

        {/* Right Column - Athlete Photo (overlays background) */}
        <div className="relative flex items-end justify-center md:justify-end overflow-hidden min-h-[350px] md:min-h-[450px]">
          {athlete.image_url && (
            <img
              src={athlete.image_url}
              alt={athlete.name}
              className="relative z-10 h-full w-auto object-contain object-bottom drop-shadow-2xl max-h-[450px]"
            />
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 5: RECENT VIDEOS
// ============================================
const RecentVideosSection = ({ recordings, adminVideos, sectionTitle }) => {
  const [activeVideo, setActiveVideo] = useState(null);

  const getVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const hasAdminVideos = adminVideos && adminVideos.length > 0;
  const videosToShow = hasAdminVideos ? adminVideos : recordings;

  if (!videosToShow.length) return null;

  return (
    <section className="py-12 md:py-16 bg-black" data-testid="videos-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl md:text-4xl font-heading uppercase tracking-tight text-white">
            {sectionTitle || "Recent Videos"}
          </h2>
          <Link to="/watch" className="text-orange-500 hover:text-orange-400 flex items-center gap-2 text-sm uppercase font-bold">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {hasAdminVideos ? (
            videosToShow.slice(0, 8).map((video, idx) => {
              const videoId = getVideoId(video.url);
              if (!videoId) return null;
              return (
                <button
                  key={idx}
                  onClick={() => setActiveVideo(videoId)}
                  className="group relative aspect-video overflow-hidden rounded-lg bg-zinc-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid={`video-thumb-${idx}`}
                >
                  <img
                    src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-orange-500/90 flex items-center justify-center backdrop-blur-sm">
                      <Play className="w-5 h-5 md:w-6 md:h-6 text-black fill-black ml-0.5" />
                    </div>
                  </div>
                </button>
              );
            })
          ) : (
            recordings.slice(0, 8).map((video) => {
              const videoId = getVideoId(video.youtube_url || video.youtube_vod_url || "");
              return (
                <button
                  key={video.id}
                  onClick={() => videoId ? setActiveVideo(videoId) : null}
                  className="group relative aspect-video overflow-hidden rounded-lg bg-zinc-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid={`video-thumb-${video.id}`}
                >
                  {video.thumbnail_url || videoId ? (
                    <img
                      src={video.thumbnail_url || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { if (videoId) e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                      <Video className="w-10 h-10 text-zinc-700" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-orange-500/90 flex items-center justify-center backdrop-blur-sm">
                      <Play className="w-5 h-5 md:w-6 md:h-6 text-black fill-black ml-0.5" />
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Video Lightbox Modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
            onClick={() => setActiveVideo(null)}
            data-testid="video-lightbox"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-5xl aspect-video"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                data-testid="video-lightbox-close"
              >
                <X className="w-7 h-7" />
              </button>
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
                className="w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video Player"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ============================================
// CUSTOM SECTIONS BLOCK
// ============================================
const CustomSectionsBlock = ({ sections }) => {
  if (!sections || sections.length === 0) return null;
  
  return (
    <>
      {sections
        .filter(s => s.enabled)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(section => (
          <CustomSectionRenderer key={section.id} section={section} />
        ))
      }
    </>
  );
};

// ============================================
// SCOREBIRD GAMECAST HOMEPAGE SECTION
// ============================================
const ScoreBirdGameCastSection = ({ broadcasts }) => {
  // Find the first live (or not-started) broadcast with a scorebird_link — exclude completed
  const scorebirdBroadcast = useMemo(() => {
    if (!broadcasts?.length) return null;
    const withLink = broadcasts.filter(b => b.scorebird_link && !b.is_completed);
    if (!withLink.length) return null;
    const live = withLink.find(b => b.is_live);
    if (live) return live;
    return withLink[0];
  }, [broadcasts]);

  if (!scorebirdBroadcast) return null;

  const homeTeam = typeof scorebirdBroadcast.home_team === "object"
    ? scorebirdBroadcast.home_team
    : { name: scorebirdBroadcast.home_team || "Home" };
  const awayTeam = typeof scorebirdBroadcast.away_team === "object"
    ? scorebirdBroadcast.away_team
    : { name: scorebirdBroadcast.away_team || "Away" };

  return (
    <section className="py-8 md:py-12 bg-zinc-950" data-testid="scorebird-homepage-section">
      <div className="max-w-[1000px] mx-auto px-4 md:px-8">
        <ScoreBirdGameCast
          scorebirdLink={scorebirdBroadcast.scorebird_link}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
        <div className="flex justify-center mt-3">
          <Link
            to={`/game/${scorebirdBroadcast.id}`}
            className="inline-flex items-center gap-2 px-5 py-2 bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            data-testid="scorebird-view-game-badge"
          >
            <Play className="w-3.5 h-3.5" />
            View Game
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 6: SPONSORS CAROUSEL
// ============================================
const SponsorsSection = ({ data }) => {
  const sponsors = data?.sponsors || [];
  const title = data?.title || "Our Sponsors";
  const shouldTicker = sponsors.length > 5;
  
  // Hide section entirely when no sponsors configured
  if (sponsors.length === 0) return null;
  
  // Double the sponsors array for seamless infinite scroll
  const tickerSponsors = shouldTicker ? [...sponsors, ...sponsors] : sponsors;
  
  return (
    <section className="py-10 bg-zinc-900 border-t border-zinc-800" data-testid="sponsors-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <h3 className="text-center text-sm uppercase tracking-wider text-zinc-500 mb-6">{title}</h3>
        
        {shouldTicker ? (
          // Ticker Mode (>5 sponsors) - Continuous scrolling marquee
          <div className="relative overflow-hidden group cursor-pointer">
            <div 
              className="flex items-center gap-12 md:gap-16 animate-sponsor-ticker group-hover:pause-animation"
              style={{
                width: 'max-content',
              }}
            >
              {tickerSponsors.map((sponsor, idx) => (
                <a 
                  key={`ticker-${idx}`}
                  href={sponsor.link || '#'}
                  target={sponsor.link ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100 transform hover:scale-110"
                  title={sponsor.name}
                >
                  {sponsor.logo_url ? (
                    <img 
                      src={sponsor.logo_url} 
                      alt={sponsor.name} 
                      className="h-20 md:h-28 lg:h-32 w-auto max-w-[220px] md:max-w-[280px] object-contain pointer-events-none" 
                    />
                  ) : (
                    <span className="text-white font-bold text-lg">{sponsor.name}</span>
                  )}
                </a>
              ))}
            </div>
            
            {/* CSS Animation for Ticker */}
            <style>{`
              @keyframes sponsorTicker {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .animate-sponsor-ticker {
                animation: sponsorTicker 30s linear infinite;
              }
              .group:hover .animate-sponsor-ticker,
              .animate-sponsor-ticker.pause-animation {
                animation-play-state: paused;
              }
            `}</style>
          </div>
        ) : (
          // Grid Mode (≤5 sponsors)
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
            {sponsors.map((sponsor, idx) => (
              <a 
                key={idx}
                href={sponsor.link || '#'}
                target={sponsor.link ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="grayscale hover:grayscale-0 transition-all duration-300 opacity-70 hover:opacity-100 transform hover:scale-110"
                title={sponsor.name}
              >
                {sponsor.logo_url ? (
                  <img 
                    src={sponsor.logo_url} 
                    alt={sponsor.name} 
                    className="h-20 md:h-28 lg:h-32 w-auto max-w-[220px] md:max-w-[280px] object-contain" 
                  />
                ) : (
                  <span className="text-white font-bold text-lg">{sponsor.name}</span>
                )}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export const HomePageNew = () => {
  const [articles, setArticles] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [allBroadcasts, setAllBroadcasts] = useState([]);
  const [channels, setChannels] = useState({});
  const [recordings, setRecordings] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [liveBroadcast, setLiveBroadcast] = useState(null);
  const [featuredAthlete, setFeaturedAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionOrder, setSectionOrder] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [adminVideos, setAdminVideos] = useState([]);
  const [videosTitle, setVideosTitle] = useState("");
  const [sponsorsData, setSponsorsData] = useState({ title: "Our Sponsors", sponsors: [] });
  const [heroData, setHeroData] = useState({ 
    layout: "standard", 
    background_image: "",
    image_position_x: 50,
    image_position_y: 50,
    image_scale: 100,
    image_blur: 0,
    overlay_opacity: 70
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(checkLive, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      // Single combined API call for all homepage data
      const res = await axios.get(`${API}/settings/homepage-data`).catch(e => {
        console.warn("Failed to fetch homepage data:", e.message);
        return { data: {} };
      });
      
      const data = res.data || {};
      
      setArticles(data.articles || []);
      setRecordings((data.recordings || []).slice(0, 4));
      setFeaturedAthlete(data.featured_athlete || null);
      setSectionOrder(data.sections || []);
      setCustomSections(data.homepage?.custom_sections || []);
      
      if (data.homepage?.recent_videos?.length > 0) {
        setAdminVideos(data.homepage.recent_videos);
      }
      if (data.homepage?.videos_title) {
        setVideosTitle(data.homepage.videos_title);
      }
      if (data.homepage?.sponsors_data) {
        setSponsorsData(data.homepage.sponsors_data);
      }
      if (data.homepage?.hero_data) {
        setHeroData(data.homepage.hero_data);
      }
      
      const channelsMap = {};
      (data.channels || []).forEach(ch => {
        channelsMap[ch.id || ch.channel_id] = ch;
      });
      setChannels(channelsMap);
      
      const now = new Date();
      const allBroadcastsData = data.broadcasts || [];
      setAllBroadcasts(allBroadcastsData);
      const upcomingBroadcasts = allBroadcastsData.filter(b => {
        const broadcastDate = new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}:00`);
        return broadcastDate > now || b.is_live;
      });
      setBroadcasts(upcomingBroadcasts);
      
      if (data.live?.length > 0) {
        setLiveBroadcast(data.live[0]);
      }

      const publishedEvents = (data.events || []).filter(e => e.status === "published" || e.is_published);
      if (publishedEvents.length > 0) {
        for (const event of publishedEvents) {
          const eventBroadcasts = allBroadcastsData.filter(b => 
            b.event_id === event.id && new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}:00`) > new Date()
          );
          if (eventBroadcasts?.length > 0) {
            setNextEvent({ ...event, nextBroadcast: eventBroadcasts[0] });
            break;
          }
        }
        if (!nextEvent && upcomingBroadcasts.length > 0) {
          setNextEvent({ 
            name: upcomingBroadcasts[0].title,
            sport: upcomingBroadcasts[0].sport,
            nextBroadcast: upcomingBroadcasts[0]
          });
        }
      } else if (upcomingBroadcasts.length > 0) {
        setNextEvent({ 
          name: upcomingBroadcasts[0].title,
          sport: upcomingBroadcasts[0].sport,
          nextBroadcast: upcomingBroadcasts[0]
        });
      }
    } catch (err) {
      console.error("Error fetching homepage data:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkLive = async () => {
    try {
      const res = await axios.get(`${API}/broadcasts/live`);
      setLiveBroadcast(res.data?.length > 0 ? res.data[0] : null);
    } catch (err) {
      console.debug("Live check failed", err);
    }
  };

  // Section component mapping
  const sectionComponents = useMemo(() => ({
    hero: <NextEventSection event={nextEvent} liveBroadcast={liveBroadcast} heroData={heroData} />,
    ad_top: <AdBanner placement="homepage_top" />,
    continue_watching: <ContinueWatchingSection />,
    top_stories: <TopStoriesSection articles={articles} />,
    ad_middle: <AdBanner placement="homepage_middle" />,
    schedule: <ScheduleSection broadcasts={broadcasts} channels={channels} allBroadcasts={allBroadcasts} />,
    athletes: <AthletesSection athlete={featuredAthlete} />,
    poll: <HomepagePollSection />,
    gallery: <PhotoGallerySection />,
    videos: <RecentVideosSection recordings={recordings} adminVideos={adminVideos} sectionTitle={videosTitle} />,
    social: <SocialMediaWidget />,
    ad_bottom: <AdBanner placement="homepage_bottom" />,
    scorebird_gamecast: <ScoreBirdGameCastSection broadcasts={allBroadcasts} />,
    sponsors: <SponsorsSection data={sponsorsData} />,
    // Custom sections renderer
    custom: <CustomSectionsBlock sections={customSections} />
  }), [nextEvent, liveBroadcast, articles, broadcasts, allBroadcasts, channels, featuredAthlete, recordings, adminVideos, videosTitle, customSections, sponsorsData, heroData]);

  // Default section order - ALWAYS use this as the base
  const defaultOrder = ["hero", "continue_watching", "ad_top", "top_stories", "ad_middle", "schedule", "scorebird_gamecast", "poll", "athletes", "gallery", "videos", "custom", "social", "ad_bottom", "sponsors"];
  
  // Get ordered sections - use saved order, or defaults if nothing saved
  const orderedSections = useMemo(() => {
    // If no custom order, use defaults
    if (!sectionOrder || sectionOrder.length === 0) {
      console.log("Using default section order");
      return defaultOrder.map(id => ({ id, enabled: true }));
    }
    
    // Build a map of custom sections by ID for quick lookup
    const customSectionMap = {};
    (customSections || []).forEach(cs => {
      customSectionMap[cs.id] = cs;
    });
    
    // Process saved section order
    const finalOrder = [];
    let customSectionsIncluded = false;
    
    sectionOrder.forEach(s => {
      // Handle both string and object formats
      const sectionId = typeof s === 'string' ? s : s.id;
      const enabled = typeof s === 'string' ? true : s.enabled !== false;
      
      // Check if this is a built-in section
      if (sectionComponents[sectionId]) {
        finalOrder.push({ id: sectionId, enabled });
      }
      // Check if this is a custom section ID from the admin
      else if (customSectionMap[sectionId]) {
        // Render this specific custom section inline
        finalOrder.push({ 
          id: sectionId, 
          enabled, 
          isCustom: true, 
          customSection: customSectionMap[sectionId] 
        });
        customSectionsIncluded = true;
      }
    });
    
    // If custom sections exist but weren't in the order, add them at the end
    if (customSections && customSections.length > 0 && !customSectionsIncluded) {
      const hasCustomBlock = finalOrder.some(s => s.id === 'custom');
      if (!hasCustomBlock) {
        finalOrder.push({ id: 'custom', enabled: true });
      }
    }
    
    console.log("Section order from settings:", finalOrder.map(s => s.id));
    return finalOrder;
  }, [sectionOrder, customSections, sectionComponents]);

  // Show loading state with branded skeleton
  if (loading) {
    return (
      <div className="bg-black min-h-screen" data-testid="homepage-loading">
        {/* Skeleton hero */}
        <div className="relative h-[300px] bg-zinc-950 border-b-4 border-orange-500 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-3xl md:text-4xl uppercase text-white tracking-tight">Antler Sports Network</h1>
            <p className="text-zinc-500 mt-2">Loading...</p>
          </div>
        </div>
        {/* Skeleton content blocks */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 space-y-6">
          <div className="h-8 bg-zinc-900 w-48 animate-pulse" />
          <div className="grid md:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-48 bg-zinc-900 animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen" data-testid="homepage">
      {/* Render sections from order */}
      {orderedSections.length > 0 ? (
        orderedSections.map(section => {
          if (section.enabled === false) {
            return null;
          }
          
          // Handle inline custom sections (from admin)
          if (section.isCustom && section.customSection) {
            return (
              <div key={section.id} data-section-id={section.id}>
                <CustomSectionRenderer section={section.customSection} />
              </div>
            );
          }
          
          // Handle built-in sections
          const component = sectionComponents[section.id];
          if (!component) {
            return null;
          }
          return (
            <div key={section.id} data-section-id={section.id}>
              {component}
            </div>
          );
        })
      ) : (
        /* Fallback: If orderedSections is empty, render all defaults directly */
        <>
          <div data-section-id="hero"><NextEventSection event={nextEvent} liveBroadcast={liveBroadcast} heroData={heroData} /></div>
          <div data-section-id="top_stories"><TopStoriesSection articles={articles} /></div>
          <div data-section-id="schedule"><ScheduleSection broadcasts={broadcasts} channels={channels} allBroadcasts={allBroadcasts} /></div>
          <div data-section-id="poll"><HomepagePollSection /></div>
          <div data-section-id="athletes"><AthletesSection athlete={featuredAthlete} /></div>
          <div data-section-id="gallery"><PhotoGallerySection /></div>
          <div data-section-id="videos"><RecentVideosSection recordings={recordings} /></div>
          <div data-section-id="social"><SocialMediaWidget /></div>
          <div data-section-id="sponsors"><SponsorsSection data={sponsorsData} /></div>
          <div data-section-id="custom"><CustomSectionsBlock sections={customSections} /></div>
        </>
      )}
    </div>
  );
};

export default HomePageNew;
